/**
 * Acesso ao painel administrativo.
 *
 * Nada aqui consulta o banco direto. Quem decide se esta pessoa é
 * administradora é o banco, dentro de `fn_assert_admin`, e o cliente só
 * enxerga o resultado — não existe verificação de permissão neste arquivo,
 * de propósito: permissão conferida no navegador é permissão que se burla
 * com o console aberto.
 */
import { baseDaApi } from './presenca.ts';

export interface Alertas {
  readonly carteiras_com_drift: number;
  readonly webhooks_falhados: number;
  readonly assinaturas_invalidas_1h: number;
  readonly saques_aguardando_aprovacao: number;
  readonly partidas_em_disputa: number;
  readonly partidas_penduradas: number;
  readonly depositos_divergentes: number;
}

export interface Funil {
  readonly cobrancas_criadas: number;
  readonly cobrancas_pagas: number;
  readonly depositado_cents: number;
  readonly saques_pedidos: number;
  readonly saques_na_fila_cents: number;
  readonly partidas_criadas: number;
}

export interface LinhaFaturamento {
  readonly day: string;
  readonly stake_mode: string;
  readonly game_type: string;
  readonly matches: number;
  readonly volume_cents: number;
  readonly rake_cents: number;
}

export interface PartidaAberta {
  readonly id: string;
  readonly gameType: string;
  readonly stakeMode: string;
  readonly status: string;
  readonly entryFeeCents: number;
  readonly stakeAgreed: boolean;
  readonly createdAt: string;
  readonly idadeSegundos: number;
  readonly escrowsPresos: number;
  readonly jogadores: readonly string[];
}

export interface SinalFraude {
  readonly user_id: string;
  readonly lances: number;
  readonly lances_recusados: number;
  readonly think_medio_ms: number | null;
  readonly coef_variacao: number | null;
  readonly divergencias_relogio: number;
  readonly partidas: number;
  readonly ultimo_lance: string;
}

export interface ParSuspeito {
  readonly jogador_a: string;
  readonly jogador_b: string;
  readonly confrontos: number;
  readonly vitorias_a: number;
  readonly volume_cents: number;
}

export interface AcaoAdmin {
  readonly admin_id: string;
  readonly action: string;
  readonly target_id: string | null;
  readonly reason: string;
  readonly created_at: string;
}

export interface Painel {
  readonly geradoEm: string;
  readonly alertas: Alertas;
  readonly funil: Funil;
  readonly faturamento: readonly LinhaFaturamento[];
  readonly sinaisFraude: readonly SinalFraude[];
  readonly paresSuspeitos: readonly ParSuspeito[];
  readonly partidasAbertas: readonly PartidaAberta[];
  readonly ultimasAcoes: readonly AcaoAdmin[];
}

/** Saque esperando decisão, com o que a decisão precisa saber. */
export interface SaquePendente {
  readonly id: string;
  readonly userId: string;
  readonly displayName: string | null;
  readonly amountCents: number;
  readonly status: string;
  readonly kycStatus: string;
  /**
   * Como a identidade foi verificada: RECEITA (conferida na base) ou
   * DOCUMENTO (alguem olhou). Nulo enquanto nao foi. Sem isto, quem
   * aprova saque ve "verificado" sem saber o que sustenta a palavra.
   */
  readonly kycMethod: 'RECEITA' | 'DOCUMENTO' | null;
  readonly ambiente: string;
  readonly requestedAt: string;
  readonly sentAt: string | null;
  /**
   * Depositado, apostado e sacado. A distância entre o primeiro e o
   * segundo é o que denuncia conta usada como caixa em vez de mesa.
   */
  readonly historico: {
    readonly depositadoCents: number;
    readonly apostadoCents: number;
    readonly sacadoCents: number;
  };
}

export interface FilaDeSaques {
  readonly saques: readonly SaquePendente[];
  readonly limites: {
    readonly minimoCents: number;
    readonly maximoCents: number;
    readonly maximoDiarioCents: number;
    readonly intervaloMinutos: number;
  };
}

export type ResultadoPainel =
  | { readonly estado: 'ok'; readonly painel: Painel }
  | { readonly estado: 'sem-acesso' }
  | { readonly estado: 'erro'; readonly mensagem: string };

export async function carregarPainel(token: string): Promise<ResultadoPainel> {
  const base = baseDaApi();
  if (!base) return { estado: 'erro', mensagem: 'API não configurada nesta build.' };

  let resposta: Response;
  try {
    resposta = await fetch(`${base}/api/admin`, {
      headers: { authorization: `Bearer ${token}` },
    });
  } catch {
    return { estado: 'erro', mensagem: 'O serviço não respondeu.' };
  }

  if (resposta.status === 200) {
    return { estado: 'ok', painel: (await resposta.json()) as Painel };
  }
  if (resposta.status === 403) return { estado: 'sem-acesso' };
  if (resposta.status === 401) return { estado: 'erro', mensagem: 'Sessão expirada. Entre de novo.' };
  return { estado: 'erro', mensagem: `O serviço respondeu ${resposta.status}.` };
}

/**
 * Fila de saques.
 *
 * Consulta separada do retrato geral: carrega o histórico financeiro de
 * cada pessoa que pediu, e isso não precisa viajar toda vez que alguém
 * abre o painel.
 */
export async function carregarSaques(
  token: string,
): Promise<
  | { readonly estado: 'ok'; readonly fila: FilaDeSaques }
  | { readonly estado: 'erro'; readonly mensagem: string }
> {
  const base = baseDaApi();
  if (!base) return { estado: 'erro', mensagem: 'API não configurada nesta build.' };

  let resposta: Response;
  try {
    resposta = await fetch(`${base}/api/admin?view=withdrawals`, {
      headers: { authorization: `Bearer ${token}` },
    });
  } catch {
    return { estado: 'erro', mensagem: 'O serviço não respondeu.' };
  }

  if (resposta.status === 200) {
    return { estado: 'ok', fila: (await resposta.json()) as FilaDeSaques };
  }
  return { estado: 'erro', mensagem: `O serviço respondeu ${resposta.status}.` };
}

const MOTIVOS_ACAO: Record<string, string> = {
  NOT_AN_ADMIN: 'Você não tem acesso administrativo.',
  SELF_APPROVAL_FORBIDDEN: 'Ninguém aprova o próprio saque.',
  SELF_VERIFICATION_FORBIDDEN: 'Ninguém verifica a própria identidade.',
  WITHDRAWAL_NOT_PENDING: 'Esse saque já foi decidido.',
  WITHDRAWAL_NOT_FOUND: 'Esse saque não existe mais.',
  WITHDRAWAL_ALREADY_CLOSED: 'Esse saque já foi encerrado.',
  IDENTITY_NOT_DECLARED: 'Essa pessoa ainda não declarou os dados.',
  REASON_REQUIRED: 'Escreva o motivo.',
};

/**
 * Decide sobre um saque.
 *
 * Aprovar não envia nada: libera para a fila de despacho, que o cron
 * atende. Recusar devolve o dinheiro à carteira, por transação de
 * estorno — o ledger é insert-only, nada é apagado.
 */
export async function decidirSaque(
  token: string,
  withdrawalId: string,
  aprovar: boolean,
  motivo: string,
): Promise<ResultadoCancelamento> {
  return acaoAdmin(token, {
    action: aprovar ? 'APPROVE_WITHDRAWAL' : 'REJECT_WITHDRAWAL',
    withdrawalId,
    reason: motivo,
  });
}

/**
 * Registra que uma pessoa conferiu documento.
 *
 * A função não verifica nada — quem verifica é quem olhou. Aprovar aqui é
 * o que destrava o saque para aquela conta, então o motivo escrito é o
 * registro de que a conferência aconteceu.
 */
export async function decidirIdentidade(
  token: string,
  targetUserId: string,
  aprovar: boolean,
  motivo: string,
): Promise<ResultadoCancelamento> {
  return acaoAdmin(token, {
    action: aprovar ? 'VERIFY_KYC' : 'REJECT_KYC',
    targetUserId,
    reason: motivo,
  });
}

async function acaoAdmin(
  token: string,
  corpo: Record<string, unknown>,
): Promise<ResultadoCancelamento> {
  const base = baseDaApi();
  if (!base) return { estado: 'erro', mensagem: 'API não configurada nesta build.' };

  let resposta: Response;
  try {
    resposta = await fetch(`${base}/api/admin`, {
      method: 'POST',
      headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
      body: JSON.stringify(corpo),
    });
  } catch {
    return { estado: 'erro', mensagem: 'O serviço não respondeu.' };
  }

  if (resposta.status === 200) return { estado: 'ok' };

  const erro = (await resposta.json().catch(() => ({}))) as { error?: unknown };
  const codigo = typeof erro.error === 'string' ? erro.error : 'ERRO';
  return {
    estado: 'erro',
    mensagem: MOTIVOS_ACAO[codigo] ?? `Não foi possível concluir (${codigo}).`,
  };
}

export type ResultadoCancelamento =
  | { readonly estado: 'ok' }
  | { readonly estado: 'erro'; readonly mensagem: string };

const MOTIVOS_CANCELAMENTO: Record<string, string> = {
  NOT_AN_ADMIN: 'Você não tem acesso administrativo.',
  MATCH_NOT_CANCELLABLE: 'Essa partida já foi encerrada.',
  MATCH_NOT_FOUND: 'Essa partida não existe mais.',
  REASON_REQUIRED: 'Escreva o motivo do cancelamento.',
};

/**
 * Cancela uma partida travada. Devolve o escrow integral, sem rake, e
 * grava quem mandou — por isso o motivo é obrigatório aqui e no banco.
 */
export async function cancelarPartida(
  token: string,
  matchId: string,
  motivo: string,
): Promise<ResultadoCancelamento> {
  const base = baseDaApi();
  if (!base) return { estado: 'erro', mensagem: 'API não configurada nesta build.' };

  let resposta: Response;
  try {
    resposta = await fetch(`${base}/api/admin`, {
      method: 'POST',
      headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
      body: JSON.stringify({ action: 'CANCEL_MATCH', matchId, reason: motivo }),
    });
  } catch {
    return { estado: 'erro', mensagem: 'O serviço não respondeu.' };
  }

  if (resposta.status === 200) return { estado: 'ok' };

  const corpo = (await resposta.json().catch(() => ({}))) as { error?: unknown };
  const codigo = typeof corpo.error === 'string' ? corpo.error : 'ERRO';
  return {
    estado: 'erro',
    mensagem: MOTIVOS_CANCELAMENTO[codigo] ?? `Não foi possível cancelar (${codigo}).`,
  };
}
